<template>
  <b-container fluid>
    <b-form>
      <b-row class="form-box">
        <b-col>
          <div class="form-card">
            <span class="ap-title ap-title__section">
              <i
                class="far fa-user mr-2"
                aria-hidden="true"
              />{{ $t('global.personalData') }}
            </span>
            <b-row class="mt-2">
              <b-col
                md="12"
                lg="7"
                class="pr-5"
              >
                <b-form-group
                  :label="`${$tc('global.patientIdentifiers')}*`"
                >
                  <patient-identifier-list-input
                    ref="identifiersInput"
                    :patient-id="internalPatient.patId"
                    :patient-identifiers="internalPatient.patientIdentifiers"
                  />
                </b-form-group>
              </b-col>
              <b-col>
                <b-row>
                  <b-col
                    lg="6"
                    md="4"
                    sm="6"
                  >
                    <b-form-group
                      :label="`${$tc('global.name')}*`"
                      :state="!errors.has($tc('global.name'))"
                      :invalid-feedback="errors.first($tc('global.name'))"
                    >
                      <vt-input
                        v-model="internalPatient.patName"
                        v-testing="'name'"
                        v-validate="'required'"
                        :name="$tc('global.name')"
                        class="form-control-sm"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    lg="6"
                    md="4"
                    sm="6"
                  >
                    <b-form-group
                      :label="`${$tc('global.surname1')}*`"
                      :state="!errors.has($tc('global.surname1'))"
                      :invalid-feedback="errors.first($tc('global.surname1'))"
                    >
                      <vt-input
                        v-model="internalPatient.patSurname1"
                        v-testing="'surname1'"
                        v-validate="'required'"
                        :name="$tc('global.surname1')"
                        class="form-control-sm"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    v-if="globalParameters.middleNameSupported"
                    lg="6"
                    md="4"
                    sm="6"
                  >
                    <b-form-group :label="$tc('global.middleName')">
                      <vt-input
                        v-model="internalPatient.patMiddleName"
                        v-testing="'middlename'"
                        name="patMiddleName"
                        class="form-control-sm"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col
                    v-if="globalParameters.secondSurnameSupported"
                    lg="6"
                    md="4"
                    sm="6"
                  >
                    <b-form-group :label="$tc('global.surname2')">
                      <vt-input
                        v-model="internalPatient.patSurname2"
                        v-testing="'surname2'"
                        name="patSurname2"
                        class="form-control-sm"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
                <b-row>
                  <b-col cols="12">
                    <div class="horizontal-divider mb-3 mt-1" />
                  </b-col>
                  <b-col cols="6">
                    <b-form-group :label="$tc('global.birthday')">
                      <vt-date-picker
                        v-testing="'birthday'"
                        type="date"
                        :model="internalPatient.patBirthdate"
                        :not-after="new Date()"
                        @update="setBirthdate"
                      />
                    </b-form-group>
                  </b-col>
                  <b-col cols="6">
                    <b-form-group
                      :label="`${$tc('global.gender')}*`"
                      :state="!errors.has($tc('global.gender'))"
                      :invalid-feedback="errors.first($tc('global.gender'))"
                    >
                      <vt-combo-select
                        ref="genderCombo"
                        v-model="internalPatient.gender"
                        v-testing="'gender'"
                        v-validate="'required'"
                        :option-loader="loadGenders"
                        :allow-empty="false"
                        track-by="id"
                        label="name"
                        :name="$tc('global.gender')"
                      />
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
      <b-row
        v-if="!isEmpty(internalPatient)"
        class="mt-3"
      >
        <b-col>
          <div class="form-card">
            <span class="ap-title ap-title__section">
              <i
                class="far fa-envelope mr-2"
                aria-hidden="true"
              />{{ $t('global.contactData') }}
            </span>
            <b-row class="mt-2">
              <b-col
                lg="4"
                md="6"
              >
                <b-form-group :label="$tc('global.address')">
                  <vt-input
                    v-model="internalPatient.address.address"
                    v-testing="'address'"
                    name="address"
                    class="form-control-sm"
                  />
                </b-form-group>
              </b-col>
              <b-col
                md="2"
                sm="3"
              >
                <b-form-group :label="$tc('global.pc')">
                  <vt-input
                    v-model="internalPatient.address.postalCode"
                    v-testing="'postal-code'"
                    name="postalCode"
                    class="form-control-sm"
                  />
                </b-form-group>
              </b-col>
              <b-col
                lg="3"
                md="4"
                sm="4"
              >
                <b-form-group :label="$tc('global.country')">
                  <vt-combo-select
                    v-model="internalPatient.address.country"
                    v-testing="'country'"
                    :option-loader="loadCountries"
                    label="name"
                    track-by="id"
                    value-key="id"
                    @input="onSelectCountry"
                    @remove="clearProvince"
                  />
                </b-form-group>
              </b-col>
              <b-col
                lg="3"
                md="4"
                sm="5"
              >
                <b-form-group :label="$tc('global.province')">
                  <vt-combo-select
                    v-model="internalPatient.address.province"
                    v-testing="'province'"
                    :disabled="!internalPatient.address.country"
                    :options="provinces"
                    track-by="id"
                    value-key="id"
                    label="name"
                    @input="onSelectProvince"
                    @remove="clearMunicipality"
                  />
                </b-form-group>
              </b-col>
              <b-col
                lg="3"
                md="5"
                sm="4"
              >
                <b-form-group :label="$tc('global.municipality')">
                  <vt-combo-select
                    v-model="internalPatient.address.municipality"
                    v-testing="'municipality'"
                    :disabled="!internalPatient.address.province"
                    :options="municipalities"
                    track-by="id"
                    value-key="id"
                    label="name"
                    @input="onMunicipalityComboInput"
                  />
                </b-form-group>
              </b-col>
              <b-col
                lg="3"
                md="3"
                sm="4"
              >
                <b-form-group
                  :label="`${$tc('global.phone')} 1`"
                  :state="!errors.has(`${$tc('global.phone')} 1`)"
                  :invalid-feedback="errors.first(`${$tc('global.phone')} 1`)"
                >
                  <vt-input
                    v-model="internalPatient.contactInformation.mainPhone"
                    v-testing="'phone1'"
                    v-validate="'numeric|min:9|max:12'"
                    :name="`${$tc('global.phone')} 1`"
                    type="tel"
                    :placeholder="$tc('global.phone') + ' 1'"
                    class="form-control-sm"
                  />
                </b-form-group>
              </b-col>
              <b-col
                lg="3"
                md="3"
                sm="4"
              >
                <b-form-group
                  :label="`${$tc('global.phone')} 2`"
                  :state="!errors.has(`${$tc('global.phone')} 2`)"
                  :invalid-feedback="errors.first(`${$tc('global.phone')} 2`)"
                >
                  <vt-input
                    v-model="internalPatient.contactInformation.otherPhone1"
                    v-testing="'phone2'"
                    v-validate="'numeric|min:9|max:12'"
                    :name="`${$tc('global.phone')} 2`"
                    type="tel"
                    :placeholder="$tc('global.phone') + ' 2'"
                    class="form-control-sm"
                  />
                </b-form-group>
              </b-col>
              <b-col
                lg="3"
                md="3"
                sm="4"
              >
                <b-form-group
                  :label="`${$tc('global.phone')} 3`"
                  :state="!errors.has(`${$tc('global.phone')} 3`)"
                  :invalid-feedback="errors.first(`${$tc('global.phone')} 3`)"
                >
                  <vt-input
                    v-model="internalPatient.contactInformation.otherPhone2"
                    v-testing="'phone3'"
                    v-validate="'numeric|min:9|max:12'"
                    :name="`${$tc('global.phone')} 3`"
                    type="tel"
                    :placeholder="$tc('global.phone') + ' 3'"
                    class="form-control-sm"
                  />
                </b-form-group>
              </b-col>
              <b-col
                lg="6"
                md="6"
                sm="8"
              >
                <b-form-group
                  :label="$tc('global.email')"
                  :state="!errors.has($tc('global.email'))"
                  :invalid-feedback="errors.first($tc('global.email'))"
                >
                  <vt-input
                    v-model="internalPatient.contactInformation.email"
                    v-testing="'email'"
                    v-validate="'email'"
                    :name="$tc('global.email')"
                    type="email"
                    class="form-control-sm"
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
      <b-row class="mt-3">
        <b-col>
          <div class="form-card">
            <span class="ap-title ap-title__section">
              <i
                class="far fa-eye mr-2"
                aria-hidden="true"
              />{{ $t('global.observations') }}
            </span>
            <b-row class="mt-2">
              <b-col class="mb-2">
                <b-form-group>
                  <vt-textarea
                    v-if="notes"
                    v-model="notes.text"
                    v-testing="'observations'"
                    no-resize
                  />
                </b-form-group>
              </b-col>
            </b-row>
          </div>
        </b-col>
      </b-row>
    </b-form>
  </b-container>
</template>

<script>
import { cloneDeep } from 'lodash'
import genderResource from '@/api/resources/GenderResource'
import countryResource from '@/api/resources/CountryResource'
import provinceResource from '@/api/resources/ProvinceResource'
import municipalityResource from '@/api/resources/MunicipalityResource'
import PatientIdentifierListInput from '@/components/patient/identifier/PatientIdentifierListInput'
import { DateUtils } from '@/shared/util/date-utils'

export default {
    name: 'PatientForm',
    components: {
        PatientIdentifierListInput
    },
    props: {
        patient: { type: Object, required: true },
        notes: { type: Object, required: true }
    },
    data() {
        return {
            provinces: [],
            municipalities: [],
            internalPatient: {},
            invalid: false,
            personalDataDisplay: false
        }
    },
    watch: {
        patient() {
            this.internalPatient = cloneDeep(this.patient)
        }
    },
    created() {
        this.internalPatient = cloneDeep(this.patient)
        if (this.internalPatient.patId) {
            this.loadOptions(this.internalPatient.address?.country?.id, this.internalPatient.address?.province?.id)
        }
    },
    methods: {
        onMunicipalityComboInput() {
            // TODO: Need refactoring, use simple objects on v-model value to make data reactive
            this.$forceUpdate()
        },
        setBirthdate(birthdate) {
            if (this.internalPatient) {
                this.internalPatient.patBirthdate = DateUtils.formatDate(birthdate)
            }
        },
        async validate() {
            return this.$validator.validate()
        },
        async loadOptions(couId, proId) {
            if (couId) this.provinces = await this.loadProvinces(couId)
            if (proId) this.municipalities = await this.loadMunicipalities(proId)
        },
        async onSelectCountry(country) {
            this.provinces = await this.loadProvinces(country.id)
            this.internalPatient.address.province = null
            this.internalPatient.address.municipality = null
        },
        async onSelectProvince(province) {
            this.municipalities = await this.loadMunicipalities(province.id)
            this.internalPatient.address.municipality = null
        },
        clearProvince() {
            this.clearMunicipality()
            this.internalPatient.address.province = null
            this.provinces = []
        },
        clearMunicipality() {
            this.internalPatient.address.municipality = null
            this.municipalities = []
        },
        async loadGenders() {
            return genderResource.index()
        },
        async loadCountries() {
            return countryResource.index()
        },
        async loadProvinces(couId) {
            return provinceResource.index(couId)
        },
        async loadMunicipalities(proId) {
            return municipalityResource.index(proId)
        },
        toPatientIdentifierRequest() {
            return this.$refs.identifiersInput.toPatientIdentifierRequest()
        }
    }
}
</script>

<style scoped>
  .crud-element span{
    font-size: 0.875rem;
  }
</style>
